import React, { useCallback, useState } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { InputRadioArrowTable } from '../../../../components/display/tables/InputRadioArrowTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Styled Components
import { Container, ContainerButton } from '../components/MainStyledComponents';

// Types
import { dataTypes } from '../../../../components/display/tables/InputRadioArrowTable/types';
import { formNaming } from '../../../../components/forms/NewCommunicationForm/constants';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../gql/queries/communication';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { selectCommunicationOrders } from '../../../../gql/selectors/communications';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

export default function DuplicateCommunicationPage(): JSX.Element {
  const { t } = useTranslation();
  const data = useAsyncValue() as dataTypes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  const handleSeeCommunication = useCallback(() => {
    sessionStorage.removeItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM);
    navigate(`summary/${selectedRowId}`);
  }, [selectedRowId]);

  const handleDuplicate = useCallback(() => {
    sessionStorage.removeItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM);
    navigate(`new-communication-duplicate/${selectedRowId}`);
  }, [selectedRowId]);

  const onReFetchData = useCallback(async () => {
    const selectorCursor = getCursor(tableIds.DUPLICATE_COMMUNICATION)(store.getState());
    // Tarea #11: respetar el take del cursor (page-size selector). El selector
    // ya escribió la preferencia ahí; usarla aquí evita que el refetch reduzca
    // a `takeBase` y pierda la página completa.
    const effectiveTake = firstPageTake(selectorCursor.take, takeBase);

    const communications = await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: selectorCursor.sortField,
          orderBy: selectorCursor.orderBy ?? 'desc',
          roadId: selectorCursor.roadId,
          concessionId: selectorCursor.concessionId,
          interlocutorGroupId: selectorCursor.interlocutorGroupId,
          fromDate: selectorCursor.fromDate,
          toDate: selectorCursor.toDate,
          indexType: selectorCursor.indexType,
          indexExpedition: selectorCursor.indexExpedition,
          classification: selectorCursor.classification,
          states: selectorCursor?.states,
          userIds: selectorCursor?.userIds,
          teamIds: selectorCursor?.teamIds,
          pkInitKm: selectorCursor?.pkInitKm,
          pkInitMeter: selectorCursor?.pkInitMeter,
          pkEndKm: selectorCursor?.pkEndKm,
          pkEndMeter: selectorCursor?.pkEndMeter,
          predefinedOrderId: selectorCursor?.predefinedOrderId,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      roadId: selectorCursor?.roadId,
      concessionId: selectorCursor?.concessionId,
      interlocutorGroupId: selectorCursor?.interlocutorGroupId,
      fromDate: selectorCursor?.fromDate,
      toDate: selectorCursor?.toDate,
      indexType: selectorCursor?.indexType,
      indexExpedition: selectorCursor?.indexExpedition,
      classification: selectorCursor?.classification,
      states: selectorCursor?.states,
      userIds: selectorCursor?.userIds,
      teamIds: selectorCursor?.teamIds,
      pkInitKm: selectorCursor?.pkInitKm,
      pkInitMeter: selectorCursor?.pkInitMeter,
      pkEndKm: selectorCursor?.pkEndKm,
      pkEndMeter: selectorCursor?.pkEndMeter,
      predefinedOrderId: selectorCursor?.predefinedOrderId,
    };

    dispatch(setCursor(tableIds.DUPLICATE_COMMUNICATION, setCursorData));

    await selectCommunicationOrders(
      communications.data.communications.communications,
      true
    );

    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const selectorCursor = getCursor(tableIds.DUPLICATE_COMMUNICATION)(store.getState());
    // Tarea #11: mismo `take` que el resto del flujo — respetar la preferencia.
    const effectiveTake = firstPageTake(selectorCursor.take, takeBase);
    await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: selectorCursor.sortField,
          orderBy: selectorCursor.orderBy ?? 'desc',
          roadId: selectorCursor.roadId,
          concessionId: selectorCursor.concessionId,
          interlocutorGroupId: selectorCursor.interlocutorGroupId,
          fromDate: selectorCursor.fromDate,
          toDate: selectorCursor.toDate,
          indexType: selectorCursor.indexType,
          indexExpedition: selectorCursor.indexExpedition,
          classification: selectorCursor.classification,
          states: selectorCursor?.states,
          userIds: selectorCursor?.userIds,
          teamIds: selectorCursor?.teamIds,
          pkInitKm: selectorCursor?.pkInitKm,
          pkInitMeter: selectorCursor?.pkInitMeter,
          pkEndKm: selectorCursor?.pkEndKm,
          pkEndMeter: selectorCursor?.pkEndMeter,
          predefinedOrderId: selectorCursor?.predefinedOrderId,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);
  return (
    <Container>
      <InputRadioArrowTable
        titleText={t('communications:filterBy')}
        data={data}
        setRowState={setSelectedRowId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.DUPLICATE_COMMUNICATION}
        getDoubleTapNavigationPath={(id) => `summary/${id}`}
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('communications:view')}
          onClick={handleSeeCommunication}
          disabled={selectedRowId.length === 0}
        />
        <MainButton
          text={t('communications:duplicate')}
          onClick={handleDuplicate}
          disabled={selectedRowId.length === 0}
        />
      </ContainerButton>
    </Container>
  );
}
